import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, formatDate } from '@halo/web-lib';
import {
  Building2,
  Plus,
  Users,
  Cable,
  ChevronRight,
  ChevronDown,
  X,
  Pencil,
  UserPlus,
  Trash2,
  Search,
  Filter,
  CheckCircle2,
  AlertTriangle,
  ArrowUpDown,
  PowerOff,
  Power,
} from 'lucide-react';
import { InfoBadge } from '../components/ui/Tooltip';

import shopifyLogo from '../assets/platforms/shopify.svg';
import metaLogo from '../assets/platforms/meta.svg';
import googleAdsLogo from '../assets/platforms/google-ads.svg';
import tiktokLogo from '../assets/platforms/tiktok.svg';
import tiktokShopLogo from '../assets/platforms/tiktok-shop.svg';
import pinterestLogo from '../assets/platforms/pinterest.svg';
import klaviyoLogo from '../assets/platforms/klaviyo.svg';
import amazonAdsLogo from '../assets/platforms/amazon-ads.svg';
import amazonSellerLogo from '../assets/platforms/amazon-seller.svg';
import ga4Logo from '../assets/platforms/ga4.svg';

// ─── Platform metadata ──────────────────────────────────

const PLATFORM_META: Record<string, { label: string; icon: string }> = {
  shopify: { label: 'Shopify', icon: shopifyLogo },
  meta: { label: 'Meta Ads', icon: metaLogo },
  google_ads: { label: 'Google Ads', icon: googleAdsLogo },
  tiktok_business: { label: 'TikTok Business', icon: tiktokLogo },
  tiktok_shop: { label: 'TikTok Shop', icon: tiktokShopLogo },
  pinterest: { label: 'Pinterest', icon: pinterestLogo },
  klaviyo: { label: 'Klaviyo', icon: klaviyoLogo },
  amazon_ads: { label: 'Amazon Ads', icon: amazonAdsLogo },
  amazon_seller: { label: 'Amazon Seller', icon: amazonSellerLogo },
  ga4: { label: 'Google Analytics', icon: ga4Logo },
};

// ─── Types ────────────────────────────────────────────────

interface AgencyDto {
  id: number;
  name: string;
  slug: string;
  metadata: Record<string, unknown> | null;
  clientCount: number;
  disabledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AgencyClientMeta {
  accountManager?: string;
  clientSince?: string;
  tier?: 'starter' | 'growth' | 'enterprise';
  notes?: string;
  internalRef?: string;
}

interface AgencyClient {
  storeId: number;
  storeName: string;
  owner: { email: string; displayName: string | null } | null;
  integrations: string[];
  subscription: number;
  agencyMeta: AgencyClientMeta | null;
  createdAt: string;
}

interface AgencyClientsResponse {
  agency: { id: number; name: string; slug: string };
  clients: AgencyClient[];
}

interface AgencyMember {
  id: string;
  userId: string;
  email: string;
  displayName: string | null;
  role: 'admin' | 'manager' | 'viewer';
  grantAllStores: boolean;
  createdAt: string;
}

interface UnassignedStore {
  id: number;
  name: string;
  owner: { email: string; displayName: string | null } | null;
  subscription: number;
}

type TierValue = 'starter' | 'growth' | 'enterprise';

interface TierConfig {
  value: TierValue;
  label: string;
  color: string;
  tip: string;
}

const TIERS: readonly TierConfig[] = [
  { value: 'starter', label: 'Starter', color: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800', tip: 'Entry-level tier with basic analytics access.' },
  { value: 'growth', label: 'Growth', color: 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900/50', tip: 'Mid-tier with advanced dashboards and integrations.' },
  { value: 'enterprise', label: 'Enterprise', color: 'bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-900/50', tip: 'Full platform access with priority support and SLA guarantees.' },
] as const;

const FILTER_OPTIONS = ['All', 'Starter', 'Growth', 'Enterprise'] as const;
type FilterOption = (typeof FILTER_OPTIONS)[number];

const SORT_OPTIONS = ['Name', 'Clients', 'Created'] as const;
type SortOption = (typeof SORT_OPTIONS)[number];

interface ToastState {
  message: string;
  type: 'success' | 'error';
}

// ─── Helpers ─────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function useToast() {
  const [toast, setToast] = useState<ToastState | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  return { toast, showToast: setToast };
}

function ToastBanner({ message, type, onDismiss }: { message: string; type: 'success' | 'error'; onDismiss: () => void }) {
  return (
    <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${
      type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
    }`}>
      {type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      {message}
      <button onClick={onDismiss} className="ml-2 opacity-70 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────

export function AgencyClientsPage() {
  const { data: agencies, isLoading } = useQuery({
    queryKey: ['admin', 'agencies'],
    queryFn: async ({ signal }) => {
      const res = await api.get<AgencyDto[]>('/api/v1/admin/agencies', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load agencies');
      return res.data;
    },
  });

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterOption>('All');
  const [sortBy, setSortBy] = useState<SortOption>('Name');
  const [focusIdx, setFocusIdx] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);
  const { toast, showToast } = useToast();

  // We track all clients per agency for summary. Load them eagerly for expanded ones.
  const allAgencies = agencies ?? [];

  const summary = useMemo(() => {
    const total = allAgencies.length;
    const totalClients = allAgencies.reduce((sum, a) => sum + a.clientCount, 0);
    return { total, totalClients };
  }, [allAgencies]);

  const resultAgencies = useMemo(() => {
    let list = [...allAgencies];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.slug.toLowerCase().includes(q),
      );
    }

    // Filter is tier-based; since tier lives on clients, we keep all agencies for filter=All
    // For tier filters, we only show agencies that have clients on that tier (best effort based on name match)
    // Note: tier filtering requires client data, which is only loaded on expand. We keep filter chips for future use.

    if (sortBy === 'Created') {
      list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    } else if (sortBy === 'Clients') {
      list.sort((a, b) => b.clientCount - a.clientCount);
    } else {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }

    return list;
  }, [allAgencies, search, sortBy]);

  // Keyboard navigation
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (resultAgencies.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIdx((p) => Math.min(p + 1, resultAgencies.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIdx((p) => Math.max(p - 1, 0));
      } else if (e.key === 'Enter' && focusIdx >= 0) {
        const agency = resultAgencies[focusIdx];
        if (agency) {
          setExpandedId(expandedId === agency.id ? null : agency.id);
        }
      } else if (e.key === 'Escape') {
        setExpandedId(null);
        setFocusIdx(-1);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [resultAgencies, focusIdx, expandedId]);

  useEffect(() => {
    if (focusIdx < 0) return;
    listRef.current?.querySelector(`[data-idx="${focusIdx}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusIdx]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-0.5">Agency Clients</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage agency pods and their client stores
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-3 py-2 bg-gray-900 dark:bg-gray-950 text-white text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Agency
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Agencies</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{summary.total}</div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Clients</div>
          <div className="text-lg font-semibold text-indigo-600 dark:text-indigo-400">{summary.totalClients}</div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Avg Clients / Agency</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {summary.total > 0 ? (summary.totalClients / summary.total).toFixed(1) : '0'}
          </div>
        </div>
      </div>

      {/* Search + Filter + Sort */}
      {allAgencies.length > 0 && (
        <div className="flex items-center gap-3 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setFocusIdx(-1); }}
              placeholder="Search by agency name or slug..."
              className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Filter className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt}
                onClick={() => setFilter(opt)}
                className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                  filter === opt ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-500 dark:text-gray-400 hover:bg-gray-50'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="text-xs border border-gray-200 dark:border-gray-800 rounded-lg px-2 py-1.5 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-900"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>Sort: {opt}</option>
            ))}
          </select>
        </div>
      )}

      {/* Loading Skeletons */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-2" />
                  <div className="flex items-center gap-2">
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                    <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-16" />
                    <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-24" />
                  </div>
                </div>
                <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : allAgencies.length === 0 ? (
        /* Empty State */
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-12 text-center">
          <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">No agencies yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-sm mx-auto">
            Agencies let you group client stores under a single management pod. Create one to start organizing your client portfolio.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-gray-900 dark:bg-gray-950 text-white text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800"
          >
            Create First Agency
          </button>
        </div>
      ) : (
        <div ref={listRef} className="space-y-2">
          {resultAgencies.map((agency, idx) => (
            <AgencyCard
              key={agency.id}
              agency={agency}
              isExpanded={expandedId === agency.id}
              isFocused={focusIdx === idx}
              dataIdx={idx}
              onToggle={() => { setExpandedId(expandedId === agency.id ? null : agency.id); setFocusIdx(idx); }}
              tierFilter={filter !== 'All' ? (filter.toLowerCase() as TierValue) : null}
              showToast={showToast}
            />
          ))}
          {resultAgencies.length === 0 && (search || filter !== 'All') && (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
              <p className="text-sm text-gray-400 dark:text-gray-500">No agencies matching your filters</p>
              <button
                onClick={() => { setSearch(''); setFilter('All'); }}
                className="text-xs text-blue-600 dark:text-blue-400 mt-1 hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <CreateAgencyModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(name: string) => {
            setShowCreateModal(false);
            showToast({ message: `Agency "${name}" created successfully`, type: 'success' });
          }}
        />
      )}

      {toast && <ToastBanner message={toast.message} type={toast.type} onDismiss={() => showToast(null)} />}
    </div>
  );
}

// ─── Agency Card ──────────────────────────────────────────

function AgencyCard({
  agency,
  isExpanded,
  isFocused,
  dataIdx,
  onToggle,
  tierFilter,
  showToast,
}: {
  agency: AgencyDto;
  isExpanded: boolean;
  isFocused: boolean;
  dataIdx: number;
  onToggle: () => void;
  tierFilter: TierValue | null;
  showToast: (t: ToastState) => void;
}) {
  const queryClient = useQueryClient();

  // ── Clients query ────────────────────────────────────────
  const { data: clientsData } = useQuery({
    queryKey: ['admin', 'agencies', agency.id, 'clients'],
    queryFn: async ({ signal }) => {
      const res = await api.get<AgencyClientsResponse>(`/api/v1/admin/agencies/${agency.id}/clients`, { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load clients');
      return res.data;
    },
    enabled: isExpanded,
  });

  // ── Members query ────────────────────────────────────────
  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['admin', 'agencies', agency.id, 'members'],
    queryFn: async ({ signal }) => {
      const res = await api.get<{ agency: { id: number; name: string }; members: AgencyMember[] }>(
        `/api/v1/admin/agencies/${agency.id}/members`, { signal }
      );
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load members');
      return res.data;
    },
    enabled: isExpanded,
  });

  // ── UI state ─────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'clients' | 'members'>('clients');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAssignAdminModal, setShowAssignAdminModal] = useState(false);
  const [editingClientId, setEditingClientId] = useState<number | null>(null);

  // ── Edit agency name state ───────────────────────────────
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(agency.name);

  // ── Mutations ────────────────────────────────────────────
  const removeClientMutation = useMutation({
    mutationFn: async (storeId: number) => {
      const res = await api.delete(`/api/v1/admin/agencies/${agency.id}/clients/${storeId}`);
      if (!res.success) throw new Error(res.error ?? 'Failed to remove client');
      return res;
    },
    onSuccess: (_data, storeId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies', agency.id, 'clients'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies'] });
      showToast({ message: `Store #${storeId} removed from ${agency.name}`, type: 'success' });
    },
    onError: (err: Error) => {
      showToast({ message: err.message, type: 'error' });
    },
  });

  const assignAdminMutation = useMutation({
    mutationFn: async (email: string) => {
      const res = await api.post(`/api/v1/admin/agencies/${agency.id}/admin`, { email });
      if (!res.success) throw new Error(res.error ?? 'Failed to assign admin');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies', agency.id, 'members'] });
      showToast({ message: 'Admin assigned successfully', type: 'success' });
    },
    onError: (err: Error) => showToast({ message: err.message, type: 'error' }),
  });

  const changeRoleMutation = useMutation({
    mutationFn: async ({ membershipId, role }: { membershipId: string; role: string }) => {
      const res = await api.put(`/api/v1/admin/agencies/${agency.id}/members/${membershipId}/role`, { role });
      if (!res.success) throw new Error(res.error ?? 'Failed to change role');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies', agency.id, 'members'] });
      showToast({ message: 'Role updated', type: 'success' });
    },
    onError: (err: Error) => showToast({ message: err.message, type: 'error' }),
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (membershipId: string) => {
      const res = await api.delete(`/api/v1/admin/agencies/${agency.id}/members/${membershipId}`);
      if (!res.success) throw new Error(res.error ?? 'Failed to remove member');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies', agency.id, 'members'] });
      showToast({ message: 'Member removed', type: 'success' });
    },
    onError: (err: Error) => showToast({ message: err.message, type: 'error' }),
  });

  const updateAgencyMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await api.put(`/api/v1/admin/agencies/${agency.id}`, { name });
      if (!res.success) throw new Error(res.error ?? 'Failed to update agency');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies'] });
      setIsEditingName(false);
      showToast({ message: 'Agency name updated', type: 'success' });
    },
    onError: (err: Error) => showToast({ message: err.message, type: 'error' }),
  });

  const toggleDisabledMutation = useMutation({
    mutationFn: async (disabled: boolean) => {
      const res = await api.put(`/api/v1/admin/agencies/${agency.id}/disable`, { disabled });
      if (!res.success) throw new Error(res.error ?? 'Failed');
    },
    onSuccess: (_data, disabled) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies'] });
      showToast({ message: disabled ? 'Agency disabled' : 'Agency enabled', type: 'success' });
    },
    onError: (err: Error) => showToast({ message: err.message, type: 'error' }),
  });

  const deleteAgencyMutation = useMutation({
    mutationFn: async () => {
      const res = await api.delete(`/api/v1/admin/agencies/${agency.id}`);
      if (!res.success) throw new Error(res.error ?? 'Failed');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies'] });
      showToast({ message: `Agency "${agency.name}" deleted`, type: 'success' });
    },
    onError: (err: Error) => showToast({ message: err.message, type: 'error' }),
  });

  const handleRemoveClient = useCallback(
    (storeId: number) => {
      if (!confirm('Remove this store from the agency?')) return;
      removeClientMutation.mutate(storeId);
    },
    [removeClientMutation],
  );

  const filteredClients = useMemo(() => {
    const clients = clientsData?.clients ?? [];
    if (!tierFilter) return clients;
    return clients.filter((c) => c.agencyMeta?.tier === tierFilter);
  }, [clientsData, tierFilter]);

  const handleSaveName = () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === agency.name) {
      setIsEditingName(false);
      setEditName(agency.name);
      return;
    }
    updateAgencyMutation.mutate(trimmed);
  };

  const isDisabled = agency.disabledAt !== null;

  return (
    <div
      data-idx={dataIdx}
      className={`bg-white dark:bg-gray-900 rounded-xl border overflow-hidden transition-all hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-sm ${
        isFocused ? 'ring-2 ring-gray-900 ring-offset-1 border-gray-300' : 'border-gray-200'
      } ${isDisabled ? 'opacity-60' : ''}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isDisabled ? 'bg-gray-100' : 'bg-indigo-50'}`}>
            <Building2 className={`w-4 h-4 ${isDisabled ? 'text-gray-400' : 'text-indigo-600'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              {isDisabled && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 font-medium">Disabled</span>
              )}
              {isEditingName ? (
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleSaveName(); }
                    if (e.key === 'Escape') { e.preventDefault(); setIsEditingName(false); setEditName(agency.name); }
                  }}
                  onBlur={handleSaveName}
                  autoFocus
                  className="font-medium text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-900"
                />
              ) : (
                <>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{agency.name}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setIsEditingName(true); setEditName(agency.name); }}
                    className="p-0.5 text-gray-300 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title="Edit agency name"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                </>
              )}
              <span className="text-xs text-gray-400 dark:text-gray-500">{agency.slug}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              <InfoBadge tip={`This agency manages ${agency.clientCount} client store${agency.clientCount !== 1 ? 's' : ''}.`}>
                <span className="inline-flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {agency.clientCount} client{agency.clientCount !== 1 ? 's' : ''}
                </span>
              </InfoBadge>
              <span>&middot;</span>
              <InfoBadge tip={formatDate(agency.createdAt)}>
                <span>Created {timeAgo(agency.createdAt)}</span>
              </InfoBadge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <InfoBadge tip="Total number of client stores assigned to this agency">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{agency.clientCount}</span>
          </InfoBadge>
          <InfoBadge tip={isDisabled ? 'Enable this agency' : 'Disable this agency'}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleDisabledMutation.mutate(!isDisabled);
              }}
              disabled={toggleDisabledMutation.isPending}
              className={`p-1 rounded transition-colors ${isDisabled ? 'text-green-500 hover:text-green-700' : 'text-gray-400 hover:text-orange-500'}`}
              title={isDisabled ? 'Enable agency' : 'Disable agency'}
            >
              {isDisabled ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
            </button>
          </InfoBadge>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100 dark:border-gray-800">
          {/* Tab Bar */}
          <div className="flex items-center border-b border-gray-100 dark:border-gray-800 px-5">
            <button
              onClick={() => setActiveTab('clients')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'clients'
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600'
              }`}
            >
              Clients ({agency.clientCount})
            </button>
            <button
              onClick={() => setActiveTab('members')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'members'
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600'
              }`}
            >
              Members ({membersData?.members.length ?? 0})
            </button>

            {/* Action buttons on right side of tab bar */}
            <div className="ml-auto flex items-center gap-2">
              {activeTab === 'clients' && (
                <button
                  onClick={() => setShowAssignModal(true)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-950 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <UserPlus className="w-3.5 h-3.5" /> Add Client
                </button>
              )}
              {activeTab === 'members' && (
                <button
                  onClick={() => setShowAssignAdminModal(true)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-gray-900 dark:bg-gray-950 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800"
                >
                  <UserPlus className="w-3.5 h-3.5" /> Add Member
                </button>
              )}
              <button
                onClick={() => {
                  if (confirm(`Permanently delete "${agency.name}"? This cannot be undone. All members, invitations, and notification rules will be removed, and stores will be unassigned.`)) {
                    deleteAgencyMutation.mutate();
                  }
                }}
                disabled={deleteAgencyMutation.isPending}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 rounded-lg hover:bg-red-100 dark:hover:bg-red-950/40 disabled:opacity-50"
                title="Permanently delete this agency"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete Agency
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'clients' ? (
            <div className="px-5 py-4">
              {tierFilter && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Filtered: {tierFilter}</p>
              )}
              {!clientsData ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => (
                    <div key={i} className="border border-gray-100 dark:border-gray-800 rounded-lg p-3 animate-pulse">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                        <div className="flex-1">
                          <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-1.5" />
                          <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-48" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="py-6 text-center">
                  <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    {tierFilter
                      ? `No ${tierFilter}-tier clients in this agency.`
                      : 'No stores assigned yet. Click "Add Client" to add clients.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredClients.map((client) => (
                    <ClientRow
                      key={client.storeId}
                      client={client}
                      isEditing={editingClientId === client.storeId}
                      onEditToggle={() =>
                        setEditingClientId(editingClientId === client.storeId ? null : client.storeId)
                      }
                      onRemove={() => handleRemoveClient(client.storeId)}
                      onMetaSaved={() => {
                        setEditingClientId(null);
                        showToast({ message: `Client metadata saved for ${client.storeName}`, type: 'success' });
                      }}
                      agencyId={agency.id}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="px-5 py-3">
              {membersLoading ? (
                <div className="space-y-2 py-2">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-12 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : (membersData?.members ?? []).length === 0 ? (
                <div className="py-8 text-center">
                  <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">No members assigned</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Add an admin to give someone access to this agency</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {(membersData?.members ?? []).map((member) => (
                    <div key={member.id} className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300">
                          {(member.displayName ?? member.email)[0]?.toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {member.displayName ?? member.email}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{member.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {member.grantAllStores && (
                          <span className="text-[10px] bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded">All stores</span>
                        )}
                        <select
                          value={member.role}
                          onChange={(e) => changeRoleMutation.mutate({ membershipId: member.id, role: e.target.value })}
                          className="text-xs border border-gray-200 dark:border-gray-800 rounded-md px-2 py-1 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-900"
                        >
                          <option value="admin">Admin</option>
                          <option value="manager">Manager</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <button
                          onClick={() => {
                            if (confirm(`Remove ${member.email} from this agency?`)) {
                              removeMemberMutation.mutate(member.id);
                            }
                          }}
                          className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title="Remove member"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showAssignModal && (
        <AssignStoreModal
          agencyId={agency.id}
          agencyName={agency.name}
          onClose={() => setShowAssignModal(false)}
          onAssigned={(storeName: string) => {
            showToast({ message: `"${storeName}" assigned to ${agency.name}`, type: 'success' });
          }}
        />
      )}

      {showAssignAdminModal && (
        <AssignAdminModal
          onClose={() => setShowAssignAdminModal(false)}
          onSubmit={(email) => {
            assignAdminMutation.mutate(email, {
              onSuccess: () => setShowAssignAdminModal(false),
            });
          }}
          isPending={assignAdminMutation.isPending}
        />
      )}
    </div>
  );
}

// ─── Client Row ───────────────────────────────────────────

function ClientRow({
  client,
  isEditing,
  onEditToggle,
  onRemove,
  onMetaSaved,
  agencyId,
}: {
  client: AgencyClient;
  isEditing: boolean;
  onEditToggle: () => void;
  onRemove: () => void;
  onMetaSaved: () => void;
  agencyId: number;
}) {
  const tierConfig = TIERS.find((t) => t.value === client.agencyMeta?.tier);

  return (
    <div className="border border-gray-100 dark:border-gray-800 rounded-lg hover:border-gray-200 dark:hover:border-gray-800 transition-colors">
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
            <Users className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {client.storeName}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">#{client.storeId}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              <span className="truncate">{client.owner?.email ?? 'No owner'}</span>
              {client.agencyMeta?.accountManager && (
                <InfoBadge tip={`Account manager responsible for this client relationship.`}>
                  <span className="text-gray-400 dark:text-gray-500">
                    AM: {client.agencyMeta.accountManager}
                  </span>
                </InfoBadge>
              )}
              <span>&middot;</span>
              <InfoBadge tip={formatDate(client.createdAt)}>
                <span>{timeAgo(client.createdAt)}</span>
              </InfoBadge>
            </div>
            {/* Platform icons */}
            {client.integrations.length > 0 && (
              <div className="flex items-center gap-1 mt-1">
                {client.integrations.map((p) => {
                  const pm = PLATFORM_META[p];
                  return pm?.icon ? (
                    <InfoBadge key={p} tip={pm.label}>
                      <img src={pm.icon} alt={pm.label} className="w-3.5 h-3.5" />
                    </InfoBadge>
                  ) : (
                    <span key={p} className="text-[9px] px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">{p}</span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {tierConfig && (
            <InfoBadge tip={tierConfig.tip}>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${tierConfig.color}`}>
                {tierConfig.label}
              </span>
            </InfoBadge>
          )}
          {client.integrations.length > 0 && (
            <InfoBadge tip={`${client.integrations.length} platform integration${client.integrations.length !== 1 ? 's' : ''} connected via OAuth.`}>
              <div className="flex items-center gap-1">
                <Cable className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                <span className="text-xs text-gray-500 dark:text-gray-400">{client.integrations.length}</span>
              </div>
            </InfoBadge>
          )}
          <button
            onClick={onEditToggle}
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
            title="Edit metadata"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onRemove}
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-500 rounded transition-colors"
            title="Remove from agency"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
        </div>
      </div>

      {isEditing && (
        <ClientMetaEditor
          agencyId={agencyId}
          storeId={client.storeId}
          meta={client.agencyMeta}
          onSaved={onMetaSaved}
          onCancel={onEditToggle}
        />
      )}
    </div>
  );
}

// ─── Client Metadata Editor ──────────────────────────────

function ClientMetaEditor({
  agencyId,
  storeId,
  meta,
  onSaved,
  onCancel,
}: {
  agencyId: number;
  storeId: number;
  meta: AgencyClientMeta | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [accountManager, setAccountManager] = useState(meta?.accountManager ?? '');
  const [tier, setTier] = useState<string>(meta?.tier ?? '');
  const [internalRef, setInternalRef] = useState(meta?.internalRef ?? '');
  const [notes, setNotes] = useState(meta?.notes ?? '');

  const saveMutation = useMutation({
    mutationFn: async (agencyMeta: AgencyClientMeta) => {
      const res = await api.put(`/api/v1/admin/agencies/${agencyId}/clients/${storeId}/meta`, { agencyMeta });
      if (!res.success) throw new Error(res.error ?? 'Failed to save');
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies', agencyId, 'clients'] });
      onSaved();
    },
  });

  const handleSave = () => {
    const agencyMeta: AgencyClientMeta = {
      ...(accountManager ? { accountManager } : {}),
      ...(tier ? { tier: tier as AgencyClientMeta['tier'] } : {}),
      ...(internalRef ? { internalRef } : {}),
      ...(notes ? { notes } : {}),
      clientSince: meta?.clientSince ?? new Date().toISOString().slice(0, 10),
    };
    saveMutation.mutate(agencyMeta);
  };

  return (
    <div className="border-t border-gray-100 dark:border-gray-800 p-3 bg-gray-50 dark:bg-gray-950 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Account Manager</span>
          <input
            type="text"
            value={accountManager}
            onChange={(e) => setAccountManager(e.target.value)}
            placeholder="e.g. John Smith"
            className="mt-1 block w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-800 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Tier</span>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            className="mt-1 block w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-800 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white dark:bg-gray-900"
          >
            <option value="">Select tier...</option>
            {TIERS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Internal Reference</span>
          <input
            type="text"
            value={internalRef}
            onChange={(e) => setInternalRef(e.target.value)}
            placeholder="e.g. SCC-001"
            className="mt-1 block w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-800 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </label>
        <label className="block col-span-2">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Internal notes about this client..."
            className="mt-1 block w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-800 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
          />
        </label>
      </div>
      {saveMutation.isError && <p className="text-xs text-red-600 dark:text-red-400">{saveMutation.error.message}</p>}
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="px-3 py-1.5 text-xs font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-md hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ─── Assign Store Modal ──────────────────────────────────

function AssignStoreModal({
  agencyId,
  agencyName,
  onClose,
  onAssigned,
}: {
  agencyId: number;
  agencyName: string;
  onClose: () => void;
  onAssigned: (storeName: string) => void;
}) {
  const queryClient = useQueryClient();
  const { data: stores, isLoading } = useQuery({
    queryKey: ['admin', 'stores', 'unassigned'],
    queryFn: async ({ signal }) => {
      const res = await api.get<UnassignedStore[]>('/api/v1/admin/stores/unassigned', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load stores');
      return res.data;
    },
  });
  const [search, setSearch] = useState('');
  const [focusIdx, setFocusIdx] = useState(-1);

  const assignMutation = useMutation({
    mutationFn: async (store: UnassignedStore) => {
      const res = await api.put(`/api/v1/admin/agencies/${agencyId}/clients/${store.id}`, {
        agencyMeta: { clientSince: new Date().toISOString().slice(0, 10) },
      });
      if (!res.success) throw new Error(res.error ?? 'Failed to assign store');
      return store;
    },
    onSuccess: (store) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies', agencyId, 'clients'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores', 'unassigned'] });
      setFocusIdx(-1);
      onAssigned(store.name);
    },
  });

  const filtered = useMemo(
    () =>
      (stores ?? []).filter(
        (s) =>
          s.name.toLowerCase().includes(search.toLowerCase()) ||
          (s.owner?.email ?? '').toLowerCase().includes(search.toLowerCase()),
      ),
    [stores, search],
  );

  // Keyboard navigation within the modal
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (filtered.length === 0) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setFocusIdx((p) => Math.min(p + 1, filtered.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setFocusIdx((p) => Math.max(p - 1, 0)); }
      else if (e.key === 'Enter' && focusIdx >= 0) {
        const store = filtered[focusIdx];
        if (store && !assignMutation.isPending) {
          assignMutation.mutate(store);
        }
      } else if (e.key === 'Escape') { onClose(); }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [filtered, focusIdx, assignMutation, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Assign Store to {agencyName}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Select an unassigned store to add as an agency client
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Search by name or owner email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setFocusIdx(-1); }}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-3 border border-gray-100 dark:border-gray-800 rounded-lg animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-1.5" />
                  <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-56" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center">
              <ArrowUpDown className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {search ? 'No matching stores found' : 'All stores are already assigned to agencies'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((s, idx) => (
                <div
                  key={s.id}
                  className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950 transition-all ${
                    focusIdx === idx ? 'ring-2 ring-gray-900 ring-offset-1 border-gray-300' : 'border-gray-100'
                  }`}
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {s.name}
                      <span className="text-xs text-gray-400 dark:text-gray-500 ml-1.5">#{s.id}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{s.owner?.email ?? 'No owner'}</div>
                  </div>
                  <button
                    onClick={() => assignMutation.mutate(s)}
                    disabled={assignMutation.isPending}
                    className="px-3 py-1.5 text-xs font-medium bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-950/40 disabled:opacity-50"
                  >
                    {assignMutation.isPending && assignMutation.variables?.id === s.id ? 'Adding...' : 'Add'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Assign Admin Modal ──────────────────────────────────

function AssignAdminModal({
  onClose,
  onSubmit,
  isPending,
}: {
  onClose: () => void;
  onSubmit: (email: string) => void;
  isPending: boolean;
}) {
  const [email, setEmail] = useState('');

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md p-6 mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Add Agency Member</h3>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">Email Address</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
            autoFocus
          />
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
            The user must have an existing account. They will be assigned as an Admin with access to all stores.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-950 rounded-lg">
            Cancel
          </button>
          <button
            onClick={() => { if (email.trim()) onSubmit(email.trim()); }}
            disabled={!email.trim() || isPending}
            className="px-4 py-2 text-sm font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {isPending ? 'Assigning...' : 'Assign Admin'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Create Agency Modal ─────────────────────────────────

function CreateAgencyModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (name: string) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');

  const createMutation = useMutation({
    mutationFn: async (params: { name: string; slug: string }) => {
      const res = await api.post<AgencyDto>('/api/v1/admin/agencies', { name: params.name, slug: params.slug });
      if (!res.success) throw new Error(res.error ?? 'Failed to create agency');
      return params;
    },
    onSuccess: (params) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'agencies'] });
      onCreated(params.name);
    },
  });

  const autoSlug = (value: string) => {
    setName(value);
    setSlug(
      value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, ''),
    );
  };

  const handleCreate = () => {
    if (!name.trim() || !slug.trim()) return;
    createMutation.mutate({ name: name.trim(), slug: slug.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Create Agency</h3>
          <button onClick={onClose} className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Agency Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => autoSlug(e.target.value)}
              placeholder="e.g. Social Commerce Club"
              className="mt-1 block w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
              autoFocus
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Slug</span>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="e.g. social-commerce-club"
              className="mt-1 block w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono"
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              URL-friendly identifier. Lowercase, hyphens only.
            </p>
          </label>
          {createMutation.isError && <p className="text-sm text-red-600 dark:text-red-400">{createMutation.error.message}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={createMutation.isPending || !name.trim() || !slug.trim()}
            className="px-4 py-2 text-sm font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Agency'}
          </button>
        </div>
      </div>
    </div>
  );
}
